import { AxiosResponse } from "axios";
import { IAplosFileUploadUrlResponse } from "../models/IAplosFileUploadUrlResponse";
import { GetFileUploadUrlArgs, IGetFileUploadUrlPayload } from "../models/GetFileUploadUrlArgs";
import { getUserPath, HttpMethod, IAplosApiRequestArgs, submitApiRequest } from "@/services/aplosApi";
import { GetAplosFileArgs } from "../models/GetAplosFileArgs";
import { AplosFile } from "../models/AplosFile";
import { isEmptyOrWhitespace } from "@/utils";
import { GetFilesArgs } from "../models/GetFilesArgs";
import { ArchiveFileArgs } from "../models/ArchiveFileArgs";
import { GetFileDataResponse } from "../models/GetFileDataResponse";
import { GetAplosFileDataArgs } from "../models/GetAplosFileDataArgs";
import { GetFileDownloadUrlArgs } from "../models/GetFileDownloadUrlArgs";
import { GetFileDownloadUrlResponse } from "../models/GetFileDownloadUrlResponse";
import { ArchiveFileResponse } from "../models/ArchiveFileResponse";
import { UnarchiveFileResponse } from "../models/UnarchiveFileResponse";
import { InjectableService } from "@/shared/services/InjectableService";
import { AplosFileCategory } from "../models/AplosFileCategory";


export interface IAplosFileApiService {

    getFileUploadUrl(args: GetFileUploadUrlArgs): Promise<AxiosResponse<IAplosFileUploadUrlResponse>>;
    getFile(args: GetAplosFileArgs): Promise<AxiosResponse<AplosFile>>;
    getFileData(args: GetAplosFileDataArgs): Promise<AxiosResponse<GetFileDataResponse>>;
    getFileLineage(args: GetAplosFileArgs): Promise<AxiosResponse<Array<AplosFile>>>;
    getFiles(args: IAplosApiRequestArgs): Promise<AxiosResponse<Array<AplosFile>>>;
    getFileDownloadUrl(args: GetFileDownloadUrlArgs): Promise<AxiosResponse<GetFileDownloadUrlResponse>>;
    archiveFile(args: ArchiveFileArgs): Promise<AxiosResponse<ArchiveFileResponse>>;
    unarchiveFile(args: ArchiveFileArgs): Promise<AxiosResponse<UnarchiveFileResponse>>;
}


export class AplosFileApiService extends InjectableService implements IAplosFileApiService {

    static readonly injectableKey = 'IAplosFileApiService';

    get injectableKey() {
        return (this.constructor as typeof AplosFileApiService).injectableKey;
    }

    public async getFileUploadUrl(args: GetFileUploadUrlArgs): Promise<AxiosResponse<IAplosFileUploadUrlResponse>> {

        if (args == null) throw Error("args are required.");

        const payload: IGetFileUploadUrlPayload = {
            file_name: args.fileName,
            method_type: args.methodType,
            category: args.category
        }

        return await submitApiRequest(
            HttpMethod.POST,
            `${getUserPath(args.tenantId, args.userId)}/files`,
            payload
        );

    }


    public async getFile(args: GetAplosFileArgs): Promise<AxiosResponse<AplosFile>> {

        if (args == null) throw Error("args are required");
        if (isEmptyOrWhitespace(args.fileId)) throw Error("valid args.fileId required")

        return await submitApiRequest(
            HttpMethod.GET,
            `${getUserPath(args.tenantId, args.userId)}/files/${args.fileId}`
        );

    }

    public async getFileData(args: GetAplosFileDataArgs): Promise<AxiosResponse<GetFileDataResponse>> {

        if (args == null) throw Error("args are required");
        if (isEmptyOrWhitespace(args.fileId)) throw Error("valid args.fileId required")

        const params = new URLSearchParams()
        if (args.contentType != null) params.set('contentType', String(args.contentType))
        if (args.base64Encode != null) params.set('base64Encode', String(args.base64Encode))
        const queryString = params.toString() ? `?${params.toString()}` : ''

        return await submitApiRequest(
            HttpMethod.GET,
            `${getUserPath(args.tenantId, args.userId)}/files/${args.fileId}/stream${queryString}`
        );

    }

    public async getFileLineage(args: GetAplosFileArgs): Promise<AxiosResponse<Array<AplosFile>>> {

        if (args == null) throw Error("args are required");
        if (isEmptyOrWhitespace(args.fileId)) throw Error("valid args.fileId required")

        return await submitApiRequest(
            HttpMethod.GET,
            `${getUserPath(args.tenantId, args.userId)}/files/${args.fileId}/lineage`
        );

    }

    public async getFiles(args: GetFilesArgs): Promise<AxiosResponse<Array<AplosFile>>> {

        if (args == null) throw Error("args are required");

        const params = new URLSearchParams()
        if (args.status != null) params.set('status', String(args.status))
        if (args.category != null) {

            // Special-case for execution package output
            if (args.category === AplosFileCategory.Execution) {
                if (args.executionId == null) throw Error("executionId required with AplosFileCategory.Execution!");
                params.set('category', String(`${args.category}#${args.executionId}#results#package`))
            }
            else {
                params.set('category', String(args.category))
            }

        }

        const queryString = params.toString() ? `?${params.toString()}` : ''

        return await submitApiRequest(
            HttpMethod.GET,
            `${getUserPath(args.tenantId, args.userId)}/files${queryString}`
        );

    }

    public async getFileDownloadUrl(args: GetFileDownloadUrlArgs): Promise<AxiosResponse<GetFileDownloadUrlResponse>> {

        if (args == null) throw Error("args are required");
        if (isEmptyOrWhitespace(args.fileId)) throw Error("valid args.fileId required")

        return await submitApiRequest(
            HttpMethod.GET,
            `${getUserPath(args.tenantId, args.userId)}/files/${args.fileId}/download-url`
        );

    }

    public async archiveFile(args: ArchiveFileArgs): Promise<AxiosResponse<ArchiveFileResponse>> {

        if (args == null) throw Error("args are required");

        return await submitApiRequest(
            HttpMethod.POST,
            `${getUserPath(args.tenantId, args.userId)}/files/${args.fileId}/archive`
        );

    }

    public async unarchiveFile(args: ArchiveFileArgs): Promise<AxiosResponse<UnarchiveFileResponse>> {

        if (args == null) throw Error("args are required");

        return await submitApiRequest(
            HttpMethod.POST,
            `${getUserPath(args.tenantId, args.userId)}/files/${args.fileId}/unarchive`
        );

    }

}